/**
 * Team Aggregate Root
 * 
 * Enterprise-grade DDD implementation following Eric Evans patterns
 * NO duplicate entity files!
 */
import { AggregateRootBase } from '../../../../shared/domain/AggregateRootBase';
import { v4 as uuidv4 } from 'uuid';

// Value Objects
export class TeamName {
  constructor(
    public readonly value: string
  ) {
    if (!value || value.trim().length === 0) {
      throw new Error('Team name is required');
    }
    if (value.length > 100) {
      throw new Error('Team name cannot exceed 100 characters');
    }
  }

  equals(other: TeamName): boolean {
    return this.value === other.value;
  }
}

export class TeamPurpose {
  constructor(
    public readonly value: string
  ) {
    if (!value || value.trim().length === 0) {
      throw new Error('Team purpose is required');
    }
    if (value.length > 500) {
      throw new Error('Team purpose cannot exceed 500 characters');
    }
  }

  equals(other: TeamPurpose): boolean {
    return this.value === other.value;
  }
}

// Domain Events
export interface TeamCreated {
  aggregateId: string;
  name: string;
  purpose: string;
  occurredAt: Date;
}

export interface TeamMemberAdded {
  aggregateId: string;
  personId: string;
  role: string;
  occurredAt: Date;
}

export interface TeamMemberRemoved {
  aggregateId: string;
  personId: string;
  occurredAt: Date;
}

export interface TeamDisbanded {
  aggregateId: string;
  reason: string;
  occurredAt: Date;
}

export interface TeamLeaderChanged {
  aggregateId: string;
  oldLeaderId?: string;
  newLeaderId: string;
  occurredAt: Date;
}

// Team Member aggregate component
export class TeamMember {
  constructor(
    public readonly personId: string,
    public readonly role: string,
    public readonly joinedAt: Date
  ) {
    if (!personId) throw new Error('Person ID is required');
    if (!role) throw new Error('Role is required');
  }
}

/**
 * Team Aggregate Root
 * 
 * Represents a team of people working together
 */
export class Team extends AggregateRootBase {
  private constructor(
    id: string,
    private name: TeamName,
    private purpose: TeamPurpose,
    private organizationId?: string,
    private leaderId?: string,
    private members: Map<string, TeamMember> = new Map(),
    private isActive: boolean = true,
    private createdAt?: Date,
    private updatedAt?: Date,
    private disbandedAt?: Date,
    version: number = 0
  ) {
    super(id, version);
  }

  /**
   * Factory method to create new Team
   */
  static create(
    name: string,
    purpose: string,
    organizationId?: string
  ): Team {
    const id = uuidv4();
    const teamName = new TeamName(name);
    const teamPurpose = new TeamPurpose(purpose);
    const now = new Date();

    const team = new Team(
      id,
      teamName,
      teamPurpose,
      organizationId,
      undefined, // leaderId
      new Map(),
      true,
      now,
      now
    );

    team.addDomainEvent<TeamCreated>({
      aggregateId: id,
      name,
      purpose,
      occurredAt: now
    });

    return team;
  }

  /**
   * Reconstitute from persistence
   */
  static fromSnapshot(snapshot: any): Team {
    const members = new Map<string, TeamMember>();
    if (snapshot.members) {
      for (const member of snapshot.members) {
        members.set(member.personId, new TeamMember(
          member.personId,
          member.role,
          member.joinedAt
        ));
      }
    }

    return new Team(
      snapshot.id,
      new TeamName(snapshot.name),
      new TeamPurpose(snapshot.purpose),
      snapshot.organizationId,
      snapshot.leaderId,
      members,
      snapshot.isActive,
      snapshot.createdAt,
      snapshot.updatedAt,
      snapshot.disbandedAt,
      snapshot.version
    );
  }

  /**
   * Add a member to the team
   */
  addMember(personId: string, role: string): void {
    if (this.members.has(personId)) {
      throw new Error(`Person ${personId} is already a member of the team`);
    }

    if (!this.isActive) {
      throw new Error('Cannot add members to a disbanded team');
    }

    const member = new TeamMember(personId, role, new Date());
    this.members.set(personId, member);
    this.updatedAt = new Date();
    this.incrementVersion();

    this.addDomainEvent<TeamMemberAdded>({
      aggregateId: this.id,
      personId,
      role,
      occurredAt: new Date()
    });
  }

  /**
   * Remove a member from the team
   */
  removeMember(personId: string): void {
    if (!this.members.has(personId)) {
      throw new Error(`Person ${personId} is not a member of the team`);
    }

    if (personId === this.leaderId) {
      throw new Error('Cannot remove team leader. Change leader first.');
    }

    this.members.delete(personId);
    this.updatedAt = new Date();
    this.incrementVersion();

    this.addDomainEvent<TeamMemberRemoved>({
      aggregateId: this.id,
      personId,
      occurredAt: new Date()
    });
  }

  /**
   * Set or change the team leader
   */
  setLeader(personId: string): void {
    if (!this.members.has(personId)) {
      throw new Error('Leader must be a team member');
    }

    if (!this.isActive) {
      throw new Error('Cannot change leader of a disbanded team');
    }

    const oldLeaderId = this.leaderId;
    this.leaderId = personId;
    this.updatedAt = new Date();
    this.incrementVersion();

    this.addDomainEvent<TeamLeaderChanged>({
      aggregateId: this.id,
      oldLeaderId,
      newLeaderId: personId,
      occurredAt: new Date()
    });
  }

  /**
   * Update team details
   */
  updateDetails(name?: string, purpose?: string): void {
    let changed = false;

    if (name !== undefined && name !== this.name.value) {
      this.name = new TeamName(name);
      changed = true;
    }

    if (purpose !== undefined && purpose !== this.purpose.value) {
      this.purpose = new TeamPurpose(purpose);
      changed = true;
    }

    if (changed) {
      this.updatedAt = new Date();
      this.incrementVersion();
    }
  }

  /**
   * Disband the team
   */
  disband(reason: string): void {
    if (!this.isActive) {
      throw new Error('Team is already disbanded');
    }

    this.isActive = false;
    this.disbandedAt = new Date();
    this.updatedAt = new Date();
    this.incrementVersion();

    this.addDomainEvent<TeamDisbanded>({
      aggregateId: this.id,
      reason,
      occurredAt: new Date()
    });
  }

  /**
   * Check if a person is a member
   */
  isMember(personId: string): boolean {
    return this.members.has(personId);
  }

  /**
   * Get member count
   */
  getMemberCount(): number {
    return this.members.size;
  }

  /**
   * Get all member IDs
   */
  getMemberIds(): string[] {
    return Array.from(this.members.keys());
  }

  /**
   * Get member by ID
   */
  getMember(personId: string): TeamMember | undefined {
    return this.members.get(personId);
  }

  // Getters
  getName(): string { return this.name.value; }
  getPurpose(): string { return this.purpose.value; }
  getOrganizationId(): string | undefined { return this.organizationId; }
  getLeaderId(): string | undefined { return this.leaderId; }
  isTeamActive(): boolean { return this.isActive; }
  getCreatedAt(): Date | undefined { return this.createdAt; }
  getUpdatedAt(): Date | undefined { return this.updatedAt; }
  getDisbandedAt(): Date | undefined { return this.disbandedAt; }

  // Snapshot for persistence
  toSnapshot(): any {
    return {
      id: this.id,
      name: this.name.value,
      purpose: this.purpose.value,
      organizationId: this.organizationId,
      leaderId: this.leaderId,
      members: Array.from(this.members.values()).map(m => ({
        personId: m.personId,
        role: m.role,
        joinedAt: m.joinedAt
      })),
      isActive: this.isActive,
      createdAt: this.createdAt,
      updatedAt: this.updatedAt,
      disbandedAt: this.disbandedAt,
      version: this.version
    };
  }
}